import React from 'react';
import { Check, Lock } from 'lucide-react';

const tierFeatures = {
  tryItOut: [
    'A real campaign, written for your business',
    'Built by AI agents checking each other',
    'First email in 48 hours',
    'Nothing sent to your customers',
    'No credit card required',
  ],
  yearOne: [
    'Welcome, Nurture & Referral, Events, and Seasonal campaigns',
    'Email, Instagram, Facebook, and LinkedIn',
    'AI agents draft and review every campaign',
    'Scheduling, posting, and monitoring handled',
    'Up to 100 geo-targeted new prospective clients added to your list each quarter',
    'Live dashboard and monthly performance reporting',
  ],
};

export default function Pricing() {
  return (
    <section
      id="pricing"
      className="py-16 sm:py-20 lg:py-24 scroll-mt-24"
      style={{ backgroundColor: '#FAFAF9' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="uppercase tracking-[0.16em] text-[11px] sm:text-xs font-semibold mb-3 sm:mb-4" style={{ color: '#5132d4' }}>Pricing</p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight max-w-4xl" style={{ color: '#130F28' }}>
          Simple pricing. <span className="italic" style={{ color: '#5132d4' }}>No surprises.</span>
        </h2>
        <p className="mt-4 sm:mt-5 max-w-3xl text-base sm:text-lg" style={{ color: '#3b3656' }}>
          Free for your first year. Limited to the first 1,000 businesses to sign up. After year one, pricing is expected to start around $299/month. Cancel anytime.
        </p>

        <div className="mt-10 sm:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-7 items-stretch">
          {/* Try it Out */}
          <article
            data-testid="pricing-try-it-out"
            className="relative lift rounded-2xl p-6 sm:p-7 bg-white border flex flex-col overflow-hidden"
            style={{ borderColor: '#e6e2d6' }}
          >
            {/* Launching Q4 overlay — matches the IntakeForm ribbon */}
            <div
              data-testid="pricing-try-it-out-launching-overlay"
              role="status"
              className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl"
              style={{
                backgroundColor: 'rgba(15, 14, 26, 0.55)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
              }}
            >
              <div
                data-testid="pricing-try-it-out-launching-badge"
                className="px-5 py-3 rounded-full text-sm sm:text-base font-semibold tracking-wide inline-flex items-center gap-2 shadow-[0_12px_30px_-8px_rgba(19,15,40,0.55)] text-center"
                style={{
                  backgroundColor: '#FAFAF9',
                  color: '#130F28',
                  border: '1px solid rgba(107,75,239,0.35)',
                  maxWidth: '90%',
                }}
              >
                <Lock size={16} aria-hidden="true" style={{ color: '#6B4BEF' }} />
                <span>Launching Q4, 2026</span>
              </div>
            </div>

            <div
              data-testid="pricing-try-it-out-content"
              aria-hidden="true"
              className="flex flex-col flex-1 pointer-events-none select-none"
              style={{ filter: 'grayscale(0.15)' }}
            >
              <p className="font-semibold text-sm" style={{ color: '#4a4566' }}>Try it Out</p>
              <p className="font-serif text-4xl sm:text-5xl mt-2" style={{ color: '#130F28' }}>
                7 days<span className="text-base font-sans text-[#4a4566]"> free</span>
              </p>
              <p className="text-sm mt-1" style={{ color: '#3b3656' }}>No credit card required</p>
              <p className="text-sm mt-4" style={{ color: '#3b3656' }}>
                See a real campaign built for your business, delivered to your inbox so you see exactly what your customers will see. Nothing goes to your list until you say so.
              </p>
              <ul className="mt-5 space-y-2.5">
                {tierFeatures.tryItOut.map((f) => (
                  <li key={f} className="flex gap-2 text-sm" style={{ color: '#1f1c33' }}>
                    <Check size={16} aria-hidden="true" style={{ color: '#0a5c43' }} className="mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <span
                data-testid="pricing-try-it-out-cta"
                className="mt-auto block text-center rounded-full py-2.5 font-semibold border-2"
                style={{ borderColor: '#130F28', color: '#130F28', marginTop: '1.5rem' }}
              >
                Try it Out
              </span>
            </div>
          </article>

          {/* Year 1 Free (primary) */}
          <article
            data-testid="pricing-year-one-free"
            className="relative lift rounded-2xl p-6 sm:p-7 shadow-[0_30px_80px_-30px_rgba(107,75,239,0.7)] flex flex-col"
            style={{ backgroundColor: '#130F28', color: '#FAFAF9' }}
          >
            <span
              aria-hidden="true"
              className="absolute -top-3 left-6 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full"
              style={{ backgroundColor: '#10C98F', color: '#0a1f17' }}
            >
              Limited: first 1,000 businesses
            </span>
            <p className="font-semibold text-sm" style={{ color: '#10C98F' }}>Year 1 Free</p>
            <p className="font-serif text-4xl sm:text-5xl mt-2">
              $0<span className="text-base font-sans text-white/70">/mo for 12 months</span>
            </p>
            <p className="text-sm mt-1 text-white/75">~$299/month after year one · Cancel anytime</p>
            <p className="text-sm mt-4 text-white/85">
              Your four core campaigns, adapted across all four channels, managed start to finish for a full year. No cost, no card, no commitment.
            </p>
            <ul className="mt-5 space-y-2.5">
              {tierFeatures.yearOne.map((f) => (
                <li key={f} className="flex gap-2 text-sm text-white/95">
                  <Check size={16} aria-hidden="true" style={{ color: '#10C98F' }} className="mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <a
              href="#intake-form"
              data-testid="pricing-year-one-cta"
              className="mt-auto block text-center btn-teal rounded-full py-2.5 font-semibold focus:outline-none focus:ring-2 focus:ring-[#10C98F] focus:ring-offset-2 focus:ring-offset-[#130F28]"
              style={{ marginTop: '1.5rem' }}
            >
              Start my Year 1 free
            </a>
            <p className="mt-3 text-xs text-center text-white/70">
              Every account starts with Try it Out: a real campaign live in your first 7 days.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}
